import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/config';

interface FlowFormData {
  flowName: string;
  minQuestions: number;
  maxQuestions: number;
  contextPrompt: string;
  llmProvider: 'openai' | 'gemini';
  mandatoryQuestions: {
    initial:
      | {
          question: string;
          answers?: string[];
          type: 'single' | 'multiple' | 'number' | 'blood_pressure';
          showIfAnswer?: string | number;
        }[]
      | undefined;
    final:
      | {
          question: string;
          answers?: string[];
          type: 'single' | 'multiple' | 'number' | 'blood_pressure';
          showIfAnswer?: string | number;
        }[]
      | undefined;
  };
  openAI_API_Key: string;
  openAIModels: {
    chat: string;
    image: string;
  };
  geminiModels: {
    chat: string;
    image: string;
  };
  styleConfig: {
    roundedCorners: boolean;
    fontType: string;
    fontSize: string;
    includeImages: boolean;
    baseColors: string[];
  };
  finalScreen: {
    title: string;
    message: string;
    ctaText: string;
    ctaLink: string;
  };
  isActive: boolean;
}

const FlowBuilder: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FlowFormData>({
    shouldUnregister: true,
    defaultValues: {
      flowName: '',
      minQuestions: 3,
      maxQuestions: 10,
      mandatoryQuestions: { initial: undefined, final: undefined },
      contextPrompt: '',
      llmProvider: 'openai',
      openAI_API_Key: '',
      openAIModels: {
        chat: 'gpt-4-turbo',
        image: 'dall-e-3',
      },
      geminiModels: {
        chat: 'gemini-3.1-flash-lite',
        image: 'gemini-2.5-flash-image',
      },
      styleConfig: {
        roundedCorners: true,
        fontType: 'Inter',
        fontSize: 'medium',
        includeImages: false,
        baseColors: ['#3B82F6', '#10B981', '#F59E0B'],
      },
      finalScreen: {
        title: 'Thank you for completing the form!',
        message: 'We appreciate your responses.',
        ctaText: 'Get Started',
        ctaLink: '#',
      },
      isActive: true,
    },
  });

  const watchedValues = watch();

  useEffect(() => {
    if (id) {
      fetchFlow();
    }
  }, [id]);

  const fetchFlow = async () => {
    try {
      const response = await api.get(`/flows/${id}`);
      const flow = response.data;
      Object.keys(flow).forEach((key) => {
        if (key === 'mandatoryQuestions') {
          setValue(key as any, {
            initial: JSON.stringify(flow[key]?.initial, undefined, 4),
            final: JSON.stringify(flow[key]?.final, undefined, 4),
          });
        } else {
          setValue(key as any, flow[key]);
        }
      });
    } catch (err) {
      setError('Failed to fetch flow');
    }
  };

  const onSubmit = async (data: FlowFormData) => {
    setLoading(true);
    setError('');

    try {
      const payload = {
        ...data,
        openAI_API_Key:
          data.llmProvider === 'openai' ? data.openAI_API_Key : undefined,
        openAIModels:
          data.llmProvider === 'openai' ? data.openAIModels : undefined,
        geminiModels:
          data.llmProvider === 'gemini' ? data.geminiModels : undefined,
      };

      if (id) {
        await api.put(`/flows/${id}`, payload);
      } else {
        await api.post('/flows', payload);
      }
      navigate('/admin');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save flow');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h1 className="text-2xl font-bold text-gray-900">
                {id ? 'Edit Flow' : 'Create New Flow'}
              </h1>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              {/* Basic Information */}
              <div className="space-y-4">
                <h2 className="text-lg font-medium text-gray-900">
                  Basic Information
                </h2>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Flow Name (URL)
                  </label>
                  <input
                    type="text"
                    {...register('flowName', {
                      required: 'Flow name is required',
                      pattern: {
                        value: /^[a-z0-9-]+$/,
                        message:
                          'Only lowercase letters, numbers, and hyphens allowed',
                      },
                    })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="fitness-assessment"
                  />
                  {errors.flowName && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.flowName.message}
                    </p>
                  )}
                  <p className="mt-1 text-sm text-gray-500">
                    This will be the URL: /
                    {watchedValues.flowName || 'your-flow-name'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Minimum AI Questions
                    </label>
                    <input
                      type="number"
                      {...register('minQuestions', {
                        required: true,
                        min: 1,
                        valueAsNumber: true,
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Maximum AI Questions
                    </label>
                    <input
                      type="number"
                      {...register('maxQuestions', {
                        required: true,
                        min: 1,
                        valueAsNumber: true,
                      })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                </div>
                <p className="text-sm text-gray-500">
                  Mandatory initial and final questions are added on top of
                  this AI-generated question range.
                </p>
              </div>

              {/* Context and AI */}
              <div className="space-y-4">
                <h2 className="text-lg font-medium text-gray-900">
                  AI Configuration
                </h2>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Context Prompt
                  </label>
                  <textarea
                    {...register('contextPrompt', {
                      required: 'Context prompt is required',
                    })}
                    rows={4}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="This form should engage female leads exploring personal training..."
                  />
                  {errors.contextPrompt && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.contextPrompt.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Provider
                  </label>
                  <select
                    {...register('llmProvider')}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    <option value="openai">OpenAI</option>
                    <option value="gemini">Gemini</option>
                  </select>
                </div>

                {watchedValues.llmProvider === 'openai' ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        OpenAI API Key
                      </label>
                      <input
                        type="password"
                        {...register('openAI_API_Key', {
                          required: 'API key is required',
                        })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        placeholder="sk-..."
                      />
                      {errors.openAI_API_Key && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.openAI_API_Key.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Chat Model
                      </label>
                      <select
                        {...register('openAIModels.chat')}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      >
                        <option value="gpt-3.5-turbo">gpt-3.5-turbo</option>
                        <option value="gpt-4">gpt-4</option>
                        <option value="gpt-4-turbo">gpt-4-turbo</option>
                        <option value="gpt-4o">gpt-4o</option>
                        <option value="gpt-4o-mini">gpt-4o-mini</option>
                        <option value="gpt-4.1">gpt-4.1</option>
                        <option value="gpt-4.1-mini">gpt-4.1-mini</option>
                        <option value="gpt-4.1-nano">gpt-4.1-nano</option>
                        <option value="gpt-5">gpt-5</option>
                        <option value="gpt-5-mini">gpt-5-mini</option>
                        <option value="gpt-5-nano">gpt-5-nano</option>
                      </select>
                    </div>

                    {watchedValues.styleConfig?.includeImages && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Image Model
                        </label>
                        <select
                          {...register('openAIModels.image')}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        >
                          <option value="dall-e-2">dall-e-2</option>
                          <option value="dall-e-3">dall-e-3</option>
                        </select>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-sm text-gray-500">
                      Gemini uses the Google service account configured by the
                      backend.
                    </p>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Chat Model
                      </label>
                      <select
                        {...register('geminiModels.chat')}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      >
                        <option value="gemini-3.5-flash">
                          gemini-3.5-flash
                        </option>
                        <option value="gemini-3.1-flash-lite">
                          gemini-3.1-flash-lite
                        </option>
                        <option value="gemini-2.5-pro">gemini-2.5-pro</option>
                        <option value="gemini-2.5-flash">
                          gemini-2.5-flash
                        </option>
                        <option value="gemini-2.5-flash-lite">
                          gemini-2.5-flash-lite
                        </option>
                      </select>
                    </div>

                    {watchedValues.styleConfig?.includeImages && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Image Model
                        </label>
                        <select
                          {...register('geminiModels.image')}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        >
                          <option value="gemini-3.1-flash-image">
                            gemini-3.1-flash-image
                          </option>
                          <option value="gemini-3-pro-image">
                            gemini-3-pro-image
                          </option>
                          <option value="gemini-2.5-flash-image">
                            gemini-2.5-flash-image
                          </option>
                        </select>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Context and AI */}
              <div className="space-y-4">
                <h2 className="text-lg font-medium text-gray-900">
                  Mandatory Questions
                </h2>
                <p className="text-sm text-gray-500">
                  For a numeric answer, use <code>type: 'number'</code>.
                  No answer options are needed. Example:{' '}
                  <code>{`{ question: 'What is your height in cm?', type: 'number' }`}</code>
                </p>
                <p className="text-sm text-gray-500">
                  For blood pressure, use <code>type: 'blood_pressure'</code>
                  {' '}to show two numeric fields separated by /. The answer is
                  saved as <code>120/80</code>. Example:{' '}
                  <code>{`{ question: 'What is your blood pressure?', type: 'blood_pressure', showIfAnswer: 'Yes' }`}</code>
                </p>
                <p className="text-sm text-gray-500">
                  To make a question conditional, add{' '}
                  <code>showIfAnswer: 'Yes'</code>. It will only appear when
                  the preceding mandatory question was answered Yes.
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Initial
                  </label>
                  <textarea
                    {...register('mandatoryQuestions.initial')}
                    rows={4}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder={
                      "[\n  {\n    question: 'Do you have an injury?',\n    answers: ['Yes', 'No'],\n    type: 'single',\n  },\n  {\n    question: 'Where is the injury?',\n    answers: ['Back', 'Knee', 'Shoulder', 'Other'],\n    type: 'single',\n    showIfAnswer: 'Yes',\n  }\n]"
                    }
                  />
                  {errors.mandatoryQuestions?.initial && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.mandatoryQuestions.initial.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Final
                  </label>
                  <textarea
                    {...register('mandatoryQuestions.final')}
                    rows={4}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder={
                      "[\n  {\n    question: 'Would you like us to contact you?',\n    answers: ['Yes', 'No'],\n    type: 'single',\n  },\n  {\n    question: 'What contact method do you prefer?',\n    answers: ['Phone', 'Email'],\n    type: 'single',\n    showIfAnswer: 'Yes',\n  }\n]"
                    }
                  />
                  {errors.mandatoryQuestions?.final && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.mandatoryQuestions.final.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Style Configuration */}
              <div className="space-y-4">
                <h2 className="text-lg font-medium text-gray-900">
                  Style Configuration
                </h2>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Font Type
                    </label>
                    <select
                      {...register('styleConfig.fontType')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    >
                      <option value="Inter">Inter</option>
                      <option value="Roboto">Roboto</option>
                      <option value="Open Sans">Open Sans</option>
                      <option value="Lato">Lato</option>
                      <option value="Montserrat">Montserrat</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Font Size
                    </label>
                    <select
                      {...register('styleConfig.fontSize')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    >
                      <option value="small">Small</option>
                      <option value="medium">Medium</option>
                      <option value="large">Large</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      {...register('styleConfig.roundedCorners')}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Rounded Corners
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      {...register('styleConfig.includeImages')}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Include Images
                    </span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Base Colors
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    {[0, 1, 2].map((index) => (
                      <div key={index}>
                        <input
                          type="color"
                          {...register(
                            `styleConfig.baseColors.${index}` as const
                          )}
                          className="h-10 w-full"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Final Screen */}
              <div className="space-y-4">
                <h2 className="text-lg font-medium text-gray-900">
                  Final Screen
                </h2>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Title
                  </label>
                  <input
                    type="text"
                    {...register('finalScreen.title')}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Message
                  </label>
                  <textarea
                    {...register('finalScreen.message')}
                    rows={3}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      CTA Button Text
                    </label>
                    <input
                      type="text"
                      {...register('finalScreen.ctaText')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      CTA Link
                    </label>
                    <input
                      type="text"
                      {...register('finalScreen.ctaLink')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    {...register('isActive')}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Flow is Active
                  </span>
                </label>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate('/admin')}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : id ? 'Update Flow' : 'Create Flow'}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FlowBuilder;
